import React, { useState, useEffect, useCallback } from 'react';
import {
    Drawer, Table, Button, Space, Input, Modal, Form,
    message, Typography
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { breakService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const { Title, Text, Paragraph } = Typography;

const VIEW_STATES = {
    LIST: 'list',
    CREATE: 'create',
    EDIT: 'edit'
};

const BreaksDrawer = ({ visible, onClose }) => {
    const auth = useAuth() || {};
    const permissions = Array.isArray(auth.permissions) ? auth.permissions : [];
    const canEdit = permissions.includes('callcenter.campaigns.breaks.edit');

    const [viewState, setViewState] = useState(VIEW_STATES.LIST);
    const [loading, setLoading] = useState(false);
    const [breaks, setBreaks] = useState([]);
    const [selectedBreak, setSelectedBreak] = useState(null);
    const [form] = Form.useForm();

    const fetchBreaks = useCallback(async (search = null) => {
        setLoading(true);
        try {
            const response = await breakService.list({ search });
            if (response.status === 'success') {
                setBreaks(response.data);
            }
        } catch (error) {
            message.error('Error al cargar breaks');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (visible) {
            fetchBreaks();
            setViewState(VIEW_STATES.LIST);
            setSelectedBreak(null);
            form.resetFields();
        }
    }, [visible, fetchBreaks, form]);

    const handleCreate = () => {
        form.resetFields();
        setSelectedBreak(null);
        setViewState(VIEW_STATES.CREATE);
    };

    const handleEdit = (record) => {
        setSelectedBreak(record);
        form.setFieldsValue({
            name: record.name,
            description: record.description
        });
        setViewState(VIEW_STATES.EDIT);
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '¿Eliminar este break?',
            icon: <ExclamationCircleOutlined />,
            content: `¿Está seguro de eliminar el break "${record.name}"? Esta acción no se puede deshacer.`,
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await breakService.delete(record.id);
                    message.success('Break eliminado exitosamente');
                    fetchBreaks();
                } catch (error) {
                    message.error(error.response?.data?.message || 'Error al eliminar el break');
                }
            }
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (viewState === VIEW_STATES.CREATE) {
                await breakService.create(values);
                message.success('Break creado exitosamente');
            } else {
                await breakService.update(selectedBreak.id, values);
                message.success('Break actualizado exitosamente');
            }

            setViewState(VIEW_STATES.LIST);
            fetchBreaks();
        } catch (error) {
            if (error.errorFields) return; // Validation error
            message.error(error.response?.data?.message || 'Error al guardar el break');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setViewState(VIEW_STATES.LIST);
        form.resetFields();
    };

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Descripción',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text) => (
                <Text type="secondary">
                    {text ? (text.length > 80 ? `${text.substring(0, 80)}...` : text) : '-'}
                </Text>
            )
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            width: 100,
            render: (_, record) => canEdit && (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        title="Editar"
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                        title="Eliminar"
                    />
                </Space>
            )
        }
    ];

    const renderListView = () => (
        <>
            <div style={{ marginBottom: 16 }}>
                <Input.Search
                    placeholder="Buscar por nombre..."
                    onSearch={(value) => fetchBreaks(value)}
                    allowClear
                    style={{ width: '100%' }}
                />
            </div>
            <Table
                dataSource={breaks}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                size="small"
                locale={{ emptyText: 'No hay breaks registrados' }}
            />
        </>
    );

    const renderFormView = () => (
        <Form
            form={form}
            layout="vertical"
            style={{ marginTop: 16 }}
        >
            <Form.Item
                label="Nombre del Break"
                name="name"
                rules={[
                    { required: true, message: 'Ingrese el nombre del break' },
                    { max: 100, message: 'El nombre no puede exceder 100 caracteres' }
                ]}
            >
                <Input placeholder="Ej. Almuerzo, Descanso, Capacitación..." />
            </Form.Item>
            <Form.Item
                label="Descripción"
                name="description"
                rules={[
                    { max: 500, message: 'La descripción no puede exceder 500 caracteres' }
                ]}
            >
                <Input.TextArea
                    placeholder="Descripción opcional del break..."
                    rows={4}
                    showCount
                    maxLength={500}
                />
            </Form.Item>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
                <Button onClick={handleCancel}>
                    Cancelar
                </Button>
                <Button type="primary" onClick={handleSubmit} loading={loading}>
                    {viewState === VIEW_STATES.CREATE ? 'Crear Break' : 'Guardar Cambios'}
                </Button>
            </div>
        </Form>
    );

    const getDrawerTitle = () => {
        switch (viewState) {
            case VIEW_STATES.CREATE:
                return (
                    <Space>
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={handleCancel}
                        />
                        <span>Nuevo Break</span>
                    </Space>
                );
            case VIEW_STATES.EDIT:
                return (
                    <Space>
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={handleCancel}
                        />
                        <span>Editar Break</span>
                    </Space>
                );
            default:
                return 'Gestión de Breaks';
        }
    };

    return (
        <Drawer
            title={getDrawerTitle()}
            placement="right"
            width={480}
            onClose={onClose}
            open={visible}
            extra={
                viewState === VIEW_STATES.LIST && canEdit && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        Nuevo Break
                    </Button>
                )
            }
        >
            {viewState === VIEW_STATES.LIST ? renderListView() : renderFormView()}
        </Drawer>
    );
};

export default BreaksDrawer;
